import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';

import { useRedeemGiftCard } from '../useRedeemGiftCard';

// ────────────────────────────────────────────────────────────────────────────────

export const useRedeemGiftCardForm = (params?: useRedeemGiftCardFormParams) => {
  const { onRedeemSuccess, onRedeemError } = params ?? {};

  const { t } = useLocalizationContext();
  const { redeemGiftCard } = useRedeemGiftCard();

  const isGiftCardRegCodeEnabled = useFeatureFlag(
    'CELS-1410-enable-gift-card-reg-code',
  );
  const shouldSeparatePromoAndGiftCard = useFeatureFlag(
    'CELS-1410-separate-promo-and-gift-card',
  );

  // ────────────────────────────────────────────────────────────────────

  const requiredGiftCardMessage = t(
    'redeem-gift-card-form.error.inline.gift-card-number',
  );
  const requiredRegistrationCodeMessage = t(
    'redeem-gift-card-form.error.inline.registration-code',
  );

  const validationSchema = useMemo(() => {
    const giftCardCodeValidation = yup
      .string()
      .required(requiredGiftCardMessage);
    const registrationCodeValidation = yup
      .string()
      .required(requiredRegistrationCodeMessage);

    if (isGiftCardRegCodeEnabled) {
      return yup
        .object({
          giftCardCode: giftCardCodeValidation,
          registrationCode: registrationCodeValidation,
        })
        .required();
    }

    return yup.object({ giftCardCode: giftCardCodeValidation }).required();
  }, [
    isGiftCardRegCodeEnabled,
    requiredGiftCardMessage,
    requiredRegistrationCodeMessage,
  ]);

  // ────────────────────────────────────────────────────────────────────

  const form = useForm<RedeemGiftCardFormData>({
    defaultValues: { giftCardCode: '', registrationCode: '' },
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
  });
  const { handleSubmit } = form;

  const submitForm = useMemo(() => {
    return handleSubmit(async (formData) => {
      if (!shouldSeparatePromoAndGiftCard) return;

      const { giftCardCode, registrationCode } = formData;
      const payload = isGiftCardRegCodeEnabled
        ? { code: giftCardCode, regCode: registrationCode }
        : { code: giftCardCode };

      const response = await redeemGiftCard(payload);
      const hasRedeemedGiftCard =
        response?.redeemGiftCard.__typename === 'GiftCardBalance';

      if (!hasRedeemedGiftCard) {
        onRedeemError?.();

        return;
      }

      onRedeemSuccess?.();
      form.reset();
    });
  }, [
    form,
    handleSubmit,
    isGiftCardRegCodeEnabled,
    onRedeemError,
    onRedeemSuccess,
    redeemGiftCard,
    shouldSeparatePromoAndGiftCard,
  ]);

  // ────────────────────────────────────────────────────────────────────

  return { form, submitForm };
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type useRedeemGiftCardFormParams = Readonly<{
  onRedeemSuccess?: () => void;
  onRedeemError?: () => void;
}>;

type RedeemGiftCardFormData = Readonly<{
  giftCardCode: string;
  registrationCode?: string;
}>;
